if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.elements.viewer) {
          this.warmAfterLcp();
          this.bindViewerDrag();
        }
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      bindViewerDrag() {
        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        if (!slider) return;
        let startX;
        let startLeft;
        let lastX;
        let dragging = false;
        slider.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch' || event.button) return;
          startX = lastX = event.clientX;
          startLeft = slider.scrollLeft;
          dragging = false;
          slider.classList.add('is-dragging');
        });
        slider.addEventListener('pointermove', (event) => {
          if (startX == null) return;
          lastX = event.clientX;
          const delta = lastX - startX;
          if (!dragging) {
            if (Math.abs(delta) < 4) return;
            dragging = true;
            slider.setPointerCapture(event.pointerId);
          }
          slider.scrollLeft = startLeft - delta;
        });
        const endDrag = () => {
          if (startX == null) return;
          slider.classList.remove('is-dragging');
          if (dragging) {
            const delta = lastX - startX;
            const width = slider.clientWidth || 1;
            const slides = slider.querySelectorAll('.slider__slide');
            let index = Math.round(startLeft / width);
            if (Math.abs(delta) >= Math.min(40, width * 0.1)) index += delta < 0 ? 1 : -1;
            index = Math.max(0, Math.min(slides.length - 1, index));
            if (slides[index]) slider.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
            slider.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
            }, { capture: true, once: true });
          }
          startX = null;
          dragging = false;
        };
        slider.addEventListener('pointerup', endDrag);
        slider.addEventListener('pointercancel', endDrag);
      }

      warmAfterLcp() {
        const first = this.elements.viewer.querySelector('.product__media-item.is-active .product__media img');
        const warm = () => {
          this.elements.viewer.querySelectorAll('.product__media-item .product__media img').forEach((img) => {
            if (img.closest('.is-active')) return;
            img.loading = 'eager';
            if ('fetchPriority' in img) img.fetchPriority = 'low';
          });
        };
        if (!first || first.complete) {
          warm();
          return;
        }
        first.addEventListener('load', warm, { once: true });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.revealSeq = (this.revealSeq || 0) + 1;
        const seq = this.revealSeq;
        const img = activeMedia.querySelector('.product__media img');
        if (img) {
          img.loading = 'eager';
          if ('fetchPriority' in img) img.fetchPriority = 'high';
        }
        const reveal = () => {
          if (seq !== this.revealSeq) return;
          this.revealMedia(activeMedia, mediaId, prepend);
        };
        if (img && !img.complete) {
          img.addEventListener('load', reveal, { once: true });
          img.addEventListener('error', reveal, { once: true });
          return;
        }
        reveal();
      }

      revealMedia(activeMedia, mediaId, prepend) {
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete) {
          announce();
          return;
        }
        image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
